import type { CustomFieldDefinition } from '@fukuri-admins/apis/client/gen/model';
import { getDisplayValue } from '../getDisplayValue';

/**
 * `extractCustomFields` の結果を定義の表示形式に従ってフォーマットする
 *
 * ## ⚠️ Label 文字列突き合わせの責務
 *
 * 旧 API 設計の制約により、`CustomFieldDefinition` と `client.custom_field_values[]` の
 * 各要素を結合する共通キーが **`label` 文字列のみ** である。
 * ID 結合ができないため、この関数で `custom_field_${label}` をキーとした突き合わせを行う。
 *
 * ### 制約・リスク
 * - Label が変更されると突き合わせが失敗し、`getDisplayValue` を通らず
 *   raw value がそのまま表示される（フォールバック動作）
 * - Label が重複定義された場合、後勝ち（`definitionsByKey` の reduce が後で上書き）
 * - Label に空白・特殊文字が含まれてもキー生成は素直に文字列結合
 *
 * この杜撰さは API 設計起因であり、本関数で吸収している（他の関数では行わない）。
 *
 * @param extracted `extractCustomFields` の戻り値（`{ custom_field_${label}: value }` 形式）
 * @param definitions カスタムフィールド定義の配列
 * @returns フォーマット済みの `{ custom_field_${label}: displayValue }` 形式の Record
 */
export const formatCustomFieldValues = (
  extracted: Record<string, string>,
  definitions: CustomFieldDefinition[]
): Record<string, string> => {
  const definitionsByKey = definitions.reduce(
    (acc, field) => {
      acc[`custom_field_${field.label}`] = field;
      return acc;
    },
    {} as Record<string, CustomFieldDefinition>
  );

  return Object.entries(extracted).reduce(
    (acc, [key, value]) => {
      const definition = definitionsByKey[key];
      if (!definition) {
        acc[key] = value;
        return acc;
      }
      acc[key] = getDisplayValue(definition, value);
      return acc;
    },
    {} as Record<string, string>
  );
};
